import { memo, type MutableRefObject } from 'react';

import { HighlightedPointCanvas } from './HighlightedPointCanvas.js';
import { datapointIndicatorShiftedLayout } from './XYChartExplore.sty.js';
import { Canvas } from '../../../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../../../core/components/canvas/CanvasContext.js';
import type { CanvasShape } from '../../../../core/components/canvas/types/canvas-shape.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { POINT_OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import { useAreaData } from '../../../hooks/useAreaData.js';
import { useDotData } from '../../../hooks/useDotData.js';
import { useLineData } from '../../../hooks/useLineData.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartLinearSelection } from '../../../hooks/useXYChartLinearSelection.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { useSelectState } from '../../../store/selectors.js';
import { type HighlightedLinearPoint } from '../../../types/xy-chart-internals.js';
import { useTooltipMetadata } from '../hooks/useTooltipMetadata.js';
import {
  buildContentHighlightedPoint,
  buildSelectionHighlightedPoints,
  hasLinearContentPoint,
} from '../utils/get-selected-datapoints.js';

export const XYChartLinearPoint = memo(() => {
  const contentState = useTooltipMetadata('linear');
  const selectState = useSelectState();
  const { width, height } = useGridPlotSize();
  const axisIdToScales = useXYChartScales();
  const linearSelection = useXYChartLinearSelection();
  const { axisIdToScaleDomain } = useXYChartData();
  const { axes, datapointMetadataMap } = useXYChartData();

  const {
    seriesToMetadata: dotSeriesMeta,
    filteredSeriesWithDatapoints: dotSeriesWithDatapoints,
  } = useDotData();
  const {
    seriesToMetadata: lineSeriesMeta,
    filteredSeriesWithDatapoints: lineSeriesWithDatapoints,
  } = useLineData();
  const {
    seriesToMetadata: areaSeriesMeta,
    filteredSeriesWithDatapoints: areaSeriesWithDatapoints,
  } = useAreaData();

  const showContentPoint = hasLinearContentPoint(contentState);

  const showPoint =
    !showContentPoint &&
    (!selectState || linearSelection.selectionConfigByAxisId.size === 0);

  if (showPoint) {
    return null;
  }

  const selectionPoints = buildSelectionHighlightedPoints({
    selectState,
    linearSelection,
    width,
    axisIdToScales,
    dotSeriesWithDatapoints,
    lineSeriesWithDatapoints,
    areaSeriesWithDatapoints,
    dotSeriesMeta,
    lineSeriesMeta,
    areaSeriesMeta,
    axisIdToScaleDomain,
    axes,
    datapointMetadataMap,
  });

  const contentPoints = showContentPoint
    ? buildContentHighlightedPoint({
        contentState,
        axisIdToScales,
        dotSeriesMeta,
        lineSeriesMeta,
        areaSeriesMeta,
        axisIdToScaleDomain,
        datapointMetadataMap,
      })
    : [];

  const highlightedPoints: HighlightedLinearPoint[] = [
    ...selectionPoints,
    ...contentPoints,
  ];

  if (!highlightedPoints.length) {
    return null;
  }

  return (
    <div className={datapointIndicatorShiftedLayout}>
      <CanvasContext.Provider
        value={{
          shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
        }}
      >
        <Canvas
          width={width + 2 * POINT_OVERLAY_PADDING}
          height={height + 2 * POINT_OVERLAY_PADDING}
        >
          <HighlightedPointCanvas points={highlightedPoints} />
        </Canvas>
      </CanvasContext.Provider>
    </div>
  );
});

XYChartLinearPoint['displayName'] = 'XYChartLinearPoint';
